import { Field } from 'formular'
import L, { PathEditor } from 'leaflet'
import { useMap } from 'react-leaflet'
import { useRef, useCallback, useEffect } from 'react'

import marker from '../images/marker.svg'
import selectedMarker from '../images/selectedPlacemark.svg'


const markerIcon = L.icon({
  iconUrl: marker,
  iconSize: [ 24, 37 ], // size of the icon
  iconAnchor: [ 12, 36 ], // point of the icon which will correspond to marker's location
})

const selectedMarkerIcon = L.icon({
  iconUrl: selectedMarker,
  iconSize: [ 24, 37 ], // size of the icon
  iconAnchor: [ 12, 36 ], // point of the icon which will correspond to marker's location
})

const useEditableMapContent = (placemarks) => {
  const map = useMap()

  const selectedElementId = useRef<number>()
  const selectedElement = useRef<(L.Marker | L.Polyline | L.Polygon) & { editor: PathEditor }>()
  const selectedElementState = useRef<EditableMap.Placemark[] | EditableMap.Polyline[] | EditableMap.MultiPolygon[]>()
  const selectedField = useRef<Field<Pick<EditableMap.MapForm, 'placemarks' | 'multiPolygons' | 'polylines'>>>()

  const handleMapClick = useCallback((event) => {
    if (selectedElement?.current?.editor?.drawing()) return // prevent handle when drawing

    if (selectedElement.current instanceof L.Polyline) {
      //@ts-ignore
      selectedElement.current.editor.newShape(event.latlng)
    } else if (selectedElement.current instanceof L.Polygon) {
      //@ts-ignore
      selectedElement.current.editor.newShape(event.latlng)
    }
  }, [ selectedElement ])

  useEffect(() => {
    // initial map settings
    map.on('click', handleMapClick)
    // map.editTools.options.skipMiddleMarkers = true

    return () => {
      map.off('click')
    }
  }, [ map, handleMapClick ])

  const addEditingMapStyling = useCallback(() => {
    const mapContainer = map.getContainer()
    mapContainer.classList.add('editing')
  }, [ map ])

  const removeEditingMapStyling = useCallback(() => {
    const mapContainer = map.getContainer()
    mapContainer.classList.remove('editing')
  }, [ map ])

  const handleToggleEdit = useCallback((event) => {
    const oldElement = selectedElement.current
    const newElement = event.target

    oldElement?.toggleEdit()
    if (oldElement !== newElement) {
      if (oldElement instanceof L.Marker) {
        oldElement.setIcon(markerIcon)
      }

      selectedElement.current = newElement
      newElement.toggleEdit()

      if (newElement instanceof L.Marker) {
        newElement.setIcon(selectedMarkerIcon)
      } else {
        addEditingMapStyling()
      }
    } else {
      if (oldElement instanceof L.Marker) {
        oldElement.setIcon(markerIcon)
      } else {
        removeEditingMapStyling()
      }

      selectedElement.current = null
      selectedField.current = null
      selectedElementState.current = null
      selectedElementId.current = null
    }
  }, [ addEditingMapStyling, removeEditingMapStyling, selectedElement ])

  // TODO SET MAP EDITABLE
  // useEffect(() => {
  //   console.log({ map })
  //   if (placemarks.length === 1 && !polylines.length && !multiPolygons.length) {
  //     setSelectedField(placemarksField)
  //     setSelectedElementState(placemarks)
  //     setSelectedElementId(placemarks[0].id)
  //     // handleToggleEdit(event)
  //   }
  // }, [ placemarks, placemarksField, polylines, multiPolygons ])

  const handleGeometryClick = useCallback((event, field, state, id) => {
    if (field.state.value?.find((item) => item.id === id).uneditable) return
    if (selectedElement?.current?.editor?.drawing()) return // prevent handle when drawing

    if (
      event.target instanceof L.Path
      && (event.originalEvent.ctrlKey || event.originalEvent.metaKey)
      && event.target.editEnabled()
    ) {
      event.target.editor.newHole(event.latlng)
      return
    }

    selectedField.current = field
    selectedElementState.current = state
    selectedElementId.current = id
    handleToggleEdit(event)
  }, [ handleToggleEdit ])

  const handleGeometryUpdate = useCallback((field, state, id, newGeometry) => {
    field.set([
      ...state.slice(0, id),
      {
        ...state[id],
        geometry: newGeometry,
      },
      ...state.slice(id + 1),
    ])

    selectedElementState.current = field.state.value
  }, [])

  const handleDelete = useCallback(() => {
    // @ts-ignore
    const newState = selectedElementState.current.filter((_, id) => id !== selectedElementId.current)
    selectedField.current.set(newState)

    removeEditingMapStyling()
    selectedField.current = null
    selectedElement.current = null
    selectedElementState.current = null
    selectedElementId.current = null
  }, [ selectedElementState, selectedField, removeEditingMapStyling, selectedElementId ])

  useEffect(() => {
    if (selectedElement.current) {
      selectedElement.current.editor.reset() // workaround of vertex edit state lose on component rerender
    }
  })

  useEffect(() => {
    if (placemarks?.some(({ centerOnUpdate }) => centerOnUpdate)) {
      const placemarksToCenter = placemarks.filter(({ centerOnUpdate }) => centerOnUpdate)
      if (placemarksToCenter.length > 1) {
        console.error('More than 1 placemark with "centerOnUpdate" prop. Centering canceled')
      } else {
        const { geometry: [ lat, lng ] } = placemarksToCenter[0]
        map.fitBounds([ [ lat, lng ] ], { maxZoom: 18 })
      }
    }
  }, [ map, placemarks ])

  return {
    map,
    selectedElement,
    selectedElementState,
    selectedElementId,
    markerIcon,
    handleDelete,
    handleGeometryClick,
    handleGeometryUpdate,
    handleToggleEdit,
    handleMapClick,
  }
}

export default useEditableMapContent